import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DockviewApi } from "dockview-react";
import { addFloatingPanel } from "./utils/DockerviewSettings";

interface TickerActionMenuProps {
  ticker: string;
  anchorRect: DOMRect;
  containerApi?: DockviewApi;
  onClose: () => void;
}

// Small "Chart / Watchlist" popup shown under a clicked ticker (calendar sidebar, messages
// feed, ...). Mouse hover moves the highlight, Enter activates whichever option is
// highlighted, click activates directly.
export const TickerActionMenu = ({ ticker, anchorRect, containerApi, onClose }: TickerActionMenuProps) => {
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const options = [
    {
      label: "Chart",
      action: () => {
        if (!containerApi) return;
        addFloatingPanel(containerApi, "realtime_chart", { ticker });
      },
    },
    {
      label: "Watchlist",
      action: () => {
        if (!containerApi) return;
        addFloatingPanel(containerApi, "watchlist", { ticker });
      },
    },
  ];

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Enter") {
        e.preventDefault();
        options[highlighted].action();
        onClose();
      } else if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, options.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
      }
    }
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighted, containerApi]);

  return createPortal(
    <div
      ref={containerRef}
      className="fixed z-[9999] w-32 overflow-hidden rounded border border-gray-300 bg-white py-1 shadow-lg dark:border-neutral-600 dark:bg-neutral-900"
      style={{ top: anchorRect.bottom + 4, left: anchorRect.left }}
    >
      {options.map((opt, i) => (
        <div
          key={opt.label}
          onMouseEnter={() => setHighlighted(i)}
          onClick={() => {
            opt.action();
            onClose();
          }}
          className={`cursor-pointer px-3 py-1 text-sm ${
            highlighted === i ? "bg-blue-500 text-white" : "text-gray-800 dark:text-gray-200"
          }`}
        >
          {opt.label}
        </div>
      ))}
    </div>,
    document.body,
  );
};

interface TickerLinkProps {
  ticker: string;
  containerApi?: DockviewApi;
  className?: string;
}

// The standard way to render a ticker: clicking it opens the TickerActionMenu below it.
export const TickerLink = ({ ticker, containerApi, className = "font-bold cursor-pointer hover:underline" }: TickerLinkProps) => {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  return (
    <>
      <span
        role="button"
        className={className}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorRect(e.currentTarget.getBoundingClientRect());
        }}
      >
        {ticker}
      </span>
      {anchorRect && (
        <TickerActionMenu ticker={ticker} anchorRect={anchorRect} containerApi={containerApi} onClose={() => setAnchorRect(null)} />
      )}
    </>
  );
};
